import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../redux/store'; // Make sure to import RootState from your store
import Dropdown from '../components/common/Dropdown';
import Button from '../components/common/Button';
import { Medal, Star, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  useGetAllCoursesQuery,
  useEnrollCourseMutation,
  useRetakeCourseMutation,
} from '../redux/apiSlice';

interface Course {
  id: number;
  language: {
    id: number;
    name: string;
  };
  level: {
    id: number;
    name: string;
  };
  detail?: {
    course_completed: boolean;
    highest_medal: string;
    total_questions: number;
    total_correct_questions: number;
    time_spent: number;
    last_incomplete_question_id: number;
    last_incomplete_section_id: number;
    last_incomplete_question_attempts: number;
  };
  course_id: number;
  course_name: string;
  duration: string;
  objective: string;
  total_activity_number: number;
  progress?: number;
  timeSpent?: string;
}

const WelcomeModal = ({
  setIsModalOpen,
  user,
}: {
  setIsModalOpen: () => void;
  user: any;
}) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="flex flex-col items-center justify-center bg-white shadow-lg rounded-lg w-full max-w-md p-4 md:p-8 border border-gray-200">
        <div className="text-sm md:text-base">
          <strong>{user?.name || 'User'}!</strong> Your epic coding adventure
          begins now with My Code Dragon. Complete coding courses and earn
          Dragon Mastery Medals as you level up your coding skills. Let your
          coding journey begin!.
        </div>
        <button
          className="mt-4 bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600"
          onClick={setIsModalOpen}
        >
          Close
        </button>
      </div>
    </div>
  );
};

const Courses: React.FC = () => {
  const navigate = useNavigate();
  const userData = useSelector((state: RootState) => state.app.userData);
  const [isModalOpen, setIsModalOpen] = useState(() => {
    return !localStorage.getItem('hasSeenWelcomeModal');
  });
  const [isRetaking, setIsRetaking] = useState(false);
  const [retakeCourse] = useRetakeCourseMutation();

  // Fetch courses data from API
  const { data, isLoading, isError, error } = useGetAllCoursesQuery();
  // Initialize the enrollment mutation
  const [enrollCourse] = useEnrollCourseMutation();

  const apiCourses = data?.courses || [];

  // Access the authentication state from Redux store
  const isAuthenticated = useSelector(
    (state: RootState) => state.app.user.isAuthenticated,
  );

  // State for languages and levels dropdown options
  const [languages, setLanguages] = useState<any[]>([
    { id: '0', value: 'All Languages', label: 'All Languages' },
    { id: '1', value: 'Python', label: 'Python' },
    { id: '2', value: 'JavaScript', label: 'JavaScript' },
    { id: '3', value: 'SQL', label: 'SQL' },
  ]);

  const [levels, setLevels] = useState<any[]>([
    { id: '1', value: 'All Levels', label: 'All Levels' },
    { id: '2', value: 'beginner', label: 'Beginner' },
    { id: '3', value: 'intermediate', label: 'Intermediate' },
    { id: '4', value: 'advanced', label: 'Advanced' },
  ]);

  // Generate dropdown options from API data
  useEffect(() => {
    if (apiCourses) {
      // Extract unique languages
      const uniqueLanguages = Array.from(
        new Set(apiCourses.map((course) => JSON.stringify(course.language))),
      ).map((item) => JSON.parse(item));

      // Extract unique levels
      const uniqueLevels = Array.from(
        new Set(apiCourses.map((course) => JSON.stringify(course.level))),
      ).map((item) => JSON.parse(item));

      // Set languages dropdown options
      setLanguages([
        { id: '0', value: 'All Languages', label: 'All Languages' },
        ...uniqueLanguages.map((lang) => ({
          id: lang.id.toString(),
          value: lang.name,
          label: lang.name.charAt(0).toUpperCase() + lang.name.slice(1),
        })),
      ]);

      // Set levels dropdown options
      setLevels([
        { id: '0', value: 'All Levels', label: 'All Levels' },
        ...uniqueLevels.map((level) => ({
          id: level.id.toString(),
          value: level.name,
          label: level.name.charAt(0).toUpperCase() + level.name.slice(1),
        })),
      ]);
    }
  }, [apiCourses]);

  const [selectedLanguage, setSelectedLanguage] = useState('0');
  const [selectedLevel, setSelectedLevel] = useState('0');

  const [courses, setCourses] = useState<Course[]>([]);

  // Transform API courses data to include progress and timeSpent
  useEffect(() => {
    if (apiCourses) {
      setCourses(
        apiCourses
          .map((course) => {
            // Calculate progress based on correct questions if detail is available
            let progress = 0;
            const timeSpent = `${course.duration} Hours`;

            if (course.detail) {
              // Calculate progress as percentage of correct questions
              if (course.detail.course_completed) {
                progress = 100;
              } else {
                progress =
                  course.detail.total_questions > 0
                    ? Math.round(
                        (course.detail.total_correct_questions /
                          course.detail.total_questions) *
                          100,
                      )
                    : 0;
              }
            }

            return {
              ...course,
              progress: progress,
              timeSpent: timeSpent,
            };
          })
          .sort((a, b) => a.course_id - b.course_id),
      );
    }
  }, [apiCourses]);

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedLanguage(e.target.value);
  };

  const handleLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedLevel(e.target.value);
  };

  const handleViewDetails = (courseId: number) => {
    navigate(`/courses/${courseId}`);
  };

  const getNextCourseId = (courseId: number): number | undefined => {
    const courseIndex = courses.findIndex((course) => course.id === courseId);
    if (
      courses.length === 0 ||
      courseIndex === -1 ||
      courseIndex === courses.length - 1
    ) {
      return undefined;
    }
    if (courses[courseIndex + 1].progress === 100) {
      return getNextCourseId(courses[courseIndex + 1].id);
    }
    return courses[courseIndex + 1].id;
  };

  const handleCompletion = (course: Course) => {
    if (!isAuthenticated) return navigate('/login');

    const nextCourseId = getNextCourseId(course.id);
    if (!course?.detail?.course_completed) {
      let timeSpent = 0;
      const minutes = course.detail?.time_spent;
      if (minutes) {
        timeSpent = minutes;
      }
      navigate('/courses/coding', {
        state: {
          courseId: course.id,
          courseName: course.course_name,
          progress: course.progress,
          duration: course.duration,
          timeSpent: timeSpent,
          language: course.language.name,
          nextCourseId: nextCourseId || undefined,
          lastAttemptedSectionId: course.detail?.last_incomplete_section_id,
          lastAttemptedQuestionId: course.detail?.last_incomplete_question_id,
          lastAttemptedQuestionAttempts:
            course.detail?.last_incomplete_question_attempts,
        },
      });
    } else if (course.progress === 100) {
      navigate(`/courses/completion/${course.id}`, {
        state: {
          name: course.course_name,
          language: course.language.name,
          nextCourseId: nextCourseId || undefined,
          // medal: 'Silver',
        },
      });
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    localStorage.setItem('hasSeenWelcomeModal', userData?.id);
  };

  const handleRetakeCourse = (courseData: Course) => {
    // Set loading state
    if (!isAuthenticated) return navigate('/login');
    setIsRetaking(true);

    const nextCourseId = getNextCourseId(courseData.id);
    // Call the retake course mutation
    retakeCourse(Number(courseData.id))
      .unwrap()
      .then((response) => {
        navigate('/courses/coding', {
          state: {
            courseId: Number(courseData.id),
            courseName: courseData?.course_name,
            progress: 0,
            duration: courseData?.duration,
            timeSpent: 0,
            language: courseData?.language.name,
            nextCourseId: nextCourseId || undefined,
          },
        });
      })
      .catch((error) => {
        console.error('Error resetting course:', error);
        setIsRetaking(false);
        // Navigate anyway in case of error
        navigate(`/courses/${courseData.id}`);
      });
  };

  // Filter courses based on selected language and level
  const filteredCourses = courses
    .filter(
      (course) =>
        selectedLanguage === '0' ||
        course.language.id.toString() === selectedLanguage,
    )
    .filter(
      (course) =>
        selectedLevel === '0' || course.level.id.toString() === selectedLevel,
    );

  return (
    <div className="flex flex-col items-center justify-start bg-gradient-to-r from-pink-100 to-blue-200 min-h-screen pb-8 lg:pb-16 pt-8 lg:pt-16 px-4 lg:px-6">
      <div className="flex items-center mb-4 lg:mb-6 gap-2 lg:gap-4 w-full justify-center">
        <h1 className="text-2xl lg:text-[36px] font-bold text-blue-600 text-center">
          🎓 Fun Coding Courses for Kids!
        </h1>
      </div>

      <div className="flex flex-col lg:flex-row items-start lg:items-center mb-6 gap-3 lg:gap-6 w-full justify-center max-w-6xl">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2 w-full sm:w-auto">
          <div className="flex items-center gap-1 sm:gap-2">
            <Star className="text-yellow-500 w-4 h-4 lg:w-6 lg:h-6" />
            <span className="text-lg lg:text-[24px] font-semibold whitespace-nowrap">
              Filter by Coding Language:
            </span>
          </div>
          <Dropdown
            id="language-dropdown"
            name="language"
            options={languages}
            placeholder="All Languages"
            className="w-full sm:w-[200px] mt-1 sm:mt-0"
            value={selectedLanguage}
            onSelect={handleLanguageChange}
            disabled={isLoading}
          />
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2 w-full sm:w-auto mt-3 lg:mt-0">
          <div className="flex items-center gap-1 sm:gap-2">
            <BookOpen className="text-green-500 w-4 h-4 lg:w-6 lg:h-6" />
            <span className="text-lg lg:text-[24px] font-semibold whitespace-nowrap">
              Filter by Skill Level:
            </span>
          </div>
          <Dropdown
            id="level-dropdown"
            name="level"
            options={levels}
            placeholder="All Levels"
            className="w-full sm:w-[200px] mt-1 sm:mt-0"
            value={selectedLevel}
            onSelect={handleLevelChange}
            disabled={isLoading}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8">Loading courses...</div>
      ) : isError ? (
        <div className="text-center py-8 text-red-500">
          Error loading courses. Please try again later.
        </div>
      ) : (
        <div className="flex flex-wrap justify-center gap-4 lg:gap-6 max-w-7xl">
          {filteredCourses.length === 0 ? (
            <div className="text-center py-8">
              No courses found with the selected filters.
            </div>
          ) : (
            filteredCourses.map((course, index) => (
              <div
                key={course.id}
                className="w-full sm:w-[320px] lg:w-[350px] h-auto min-h-[300px] lg:h-[330px] p-4 lg:p-6 bg-white rounded-3xl shadow-md transform transition duration-300 hover:scale-105 hover:shadow-lg"
              >
                <div className="text-lg lg:text-[22px] h-auto lg:h-[20%] font-bold text-purple-700 mb-2">
                  <div className="flex items-center">
                    <div
                      className="flex-1 overflow-hidden"
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      <span>
                        {index + 1}. {course.course_name}
                      </span>
                    </div>
                    {course.detail?.highest_medal && (
                      <Medal
                        className={`ml-2 flex-shrink-0 ${
                          course.detail.highest_medal.toLowerCase() === 'gold'
                            ? 'text-yellow-500'
                            : course.detail.highest_medal.toLowerCase() ===
                                'silver'
                              ? 'text-gray-400'
                              : 'text-amber-700'
                        }`}
                      />
                    )}
                  </div>
                </div>
                <div className="text-md text-purple-700 flex mb-1">
                  Level:{' '}
                  {course.level.name.charAt(0).toUpperCase() +
                    course.level.name.slice(1)}
                </div>
                <div className="bg-gray-300 rounded-full h-[24px] w-full">
                  <div
                    className="bg-green-400 h-full rounded-full"
                    style={{
                      width: `${isAuthenticated ? (course.progress && course.progress > 100 ? 100 : (course.progress ?? 0)) : 0}%`,
                    }}
                  />
                </div>
                <div className="text-sm lg:text-[16px] mt-2 text-blue-600">
                  {isAuthenticated
                    ? `${course.progress && course.progress > 100 ? 100 : (course.progress ?? 0)}% Completed`
                    : '0% Completed'}
                </div>
                <div className="text-xs lg:text-[14px] bg-yellow-100 p-2 mt-2 rounded-md font-medium">
                  ⏱ Duration:{' '}
                  {isAuthenticated
                    ? course.timeSpent
                    : `${course.duration} Hours`}
                </div>
                <div
                  className="text-sm lg:text-[16px] mt-4 bg-blue-100 p-2 rounded-md cursor-pointer text-blue-800 font-bold hover:bg-blue-200 transition"
                  onClick={() => handleViewDetails(course.id)}
                >
                  View Course Details
                </div>
                <div className="flex justify-center mt-4">
                  {course?.detail?.course_completed ? (
                    <Button
                      onClick={() => handleRetakeCourse(course)}
                      className={`w-full sm:w-[130px] ${isAuthenticated ? 'bg-pink-400 text-white' : 'bg-gray-400 text-gray-600'} rounded-full hover:bg-pink-500`}
                      disabled={isRetaking}
                    >
                      {isRetaking ? 'Retaking...' : '🚀 Retake'}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => handleCompletion(course)}
                      className={`w-full sm:w-[130px] ${isAuthenticated ? 'bg-pink-400 text-white' : 'bg-gray-400 text-gray-600'} rounded-full hover:bg-pink-500`}
                    >
                      {course.progress === 0 || !isAuthenticated
                        ? '🚀 Launch'
                        : '🏆 Continue'}
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
      {isModalOpen && isAuthenticated && (
        <WelcomeModal setIsModalOpen={handleCloseModal} user={userData} />
      )}
    </div>
  );
};

export default Courses;
